<template>
  <div v-loading="loading" class="flex h-full flex-col">
    <BasicForm
      class="rounded-b-4px bg-white"
      :class="[{ 'flex-grow': !hasTabBar && !$slots.customTab }]"
      v-bind="bindFormEvents"
      @register="registerForm"
    >
      <template v-for="item in Object.keys($slots)" #[item]="data">
        <slot :name="item" v-bind="data || {}"></slot>
      </template>
    </BasicForm>

    <slot
      name="customTab"
      v-bind="{ pageMode, pageData, setTabData, getPageData, formAction }"
    ></slot>

    <div
      v-if="hasTabBar"
      class="flex flex-1 flex-col"
      :class="{
        'mt-6px px-16px py-8px rounded-4px bg-[#fff]': PCMode,
        'tabs-nav-hidden': cachedProps?.oneTabHidden,
      }"
    >
      <slot
        name="tabsBefore"
        v-bind="{ pageMode, pageData, setTabData, getPageData, formAction }"
      ></slot>
      <Tabs
        v-bind="cachedProps?.formTabsProps"
        v-model:activeKey="activeTab"
        class="mts-basic-tabs w-full"
        size="small"
      >
        <Tabs.TabPane v-for="pane in tabPanes" :key="pane.tabKey" :tab="t(pane.tabName)">
          <TabTable
            v-if="!pane.ignoreRender"
            v-bind="{ ...pane, pageMode, pageData, setTabData, getPageData, formAction }"
          />
          <slot
            name="tabPane"
            v-bind="{ ...pane, pageMode, pageData, setTabData, getPageData, formAction }"
          ></slot>
        </Tabs.TabPane>
      </Tabs>
      <slot
        name="tabsAfter"
        v-bind="{ pageMode, pageData, setTabData, getPageData, formAction }"
      ></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { Tabs } from 'ant-design-vue';
  import { computed, ref } from 'vue';
  import type { Option } from '@mts-fe/types';
  import { omit } from '@mts-fe/utils';
  import { BasicForm, useForm } from '@mts/components/Form';
  import { t } from '@mts/hooks/web/useI18n';
  import { FORM_EMIT_EVENTS, useFormEvents } from '@mts/render/hooks/useRenderForm';
  import { PCMode } from '@mts/utils/env';
  import { FormTabPane, FormWithTabsProps, getDictOptions, PageMode } from '..';
  import TabTable from './TabTable.vue';

  defineOptions({
    name: 'FormWithTabs',
  });

  const emit = defineEmits(FORM_EMIT_EVENTS);
  const bindFormEvents = useFormEvents(emit);
  const tabPanes = ref<FormTabPane[]>([]);
  const hasTabBar = computed(() => !!tabPanes.value.length);
  const activeTab = ref<string>();
  const pageData = ref<Recordable>({});
  const setTabData = (key: string, value: any) => {
    pageData.value[key] = value;
    console.log('setTabData', key, value, pageData.value);
  };
  const getPageData = () => ({ ...pageData.value, ...formAction.getFieldsValue() });

  const [registerForm, formAction] = useForm();

  const loading = ref(false);
  const pageMode = ref<PageMode>('Add');
  const cachedProps = ref<FormWithTabsProps>();
  const reload = async () => cachedProps.value && setCompProps(cachedProps.value);
  const setCompProps = async (props: FormWithTabsProps) => {
    pageMode.value = props.pageMode || 'Add';
    cachedProps.value = props;

    const { childTabs = [], record, searchInfo = {}, defaultTabKey } = props;
    try {
      await formAction.setProps({ ...props.mainForm });
      await formAction.resetFields();
      pageData.value = {};

      if (record?.id && typeof props.api === 'function') {
        const params = { modelId: props.modelId };
        const data = { id: record.id, ...searchInfo };
        Object.assign(params, props.mainForm?.flatParam ? data : { data });
        loading.value = true;
        props
          .api(params)
          .then((res) => {
            const data = props.pageMode === 'Copy' ? omit(res, ['id']) : res;
            pageData.value = { ...data };
            formAction.setFieldsValue({ ...data }, { autoRelate: true });
            updateStatusInfo();
          })
          .finally(() => (loading.value = false));
      } else {
        const data = props.pageMode === 'Copy' ? omit(record!, ['id']) : record;
        pageData.value = { ...data };
        formAction.setFieldsValue({ ...data }, { autoRelate: true });
        updateStatusInfo();
      }

      tabPanes.value = childTabs;
      const tabIndex = childTabs.findIndex((i) => i.tabKey === defaultTabKey);
      activeTab.value = childTabs[tabIndex >= 0 ? tabIndex : 0]?.tabKey;
    } catch (error) {
      console.error(error);
    }
  };

  const statusInfo = defineModel<{ status: string; options: readonly Option[] }>('pageTag', {
    default: { status: '', options: [] },
  });
  const updateStatusInfo = async () => {
    const { showAsPageTag, schemas = [] } = cachedProps.value?.mainForm || {};
    if (!showAsPageTag) {
      return;
    }

    statusInfo.value.status = pageData.value[showAsPageTag];
    const statusConfig = schemas.find((i) => i.field === showAsPageTag);
    if (statusConfig && !statusInfo.value.options?.length) {
      statusInfo.value.options = await getDictOptions({
        modelFieldId: statusConfig.modelFieldId,
        dataTypeCode: statusConfig.dataTypeCode,
      });
    }
  };

  defineExpose({
    ...formAction,
    setCompProps,
    reload,
    getPageData,
  });
</script>

<style lang="less">
  .tabs-nav-hidden .ant-tabs > .ant-tabs-nav {
    display: none;
  }
</style>
